import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, FileText, Download, Loader2, AlertCircle, Copy } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { API, useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

export default function LessonPlanPage() {
  const { user, login } = useAuth();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    plan_type: 'full',
    class_num: parseInt(sp.get('class') || '5', 10),
    subject_name: sp.get('subject') || 'ଗଣିତ',
    chapter_title: sp.get('chapter') || '',
    topic_title: sp.get('topic') || '',
    date: new Date().toISOString().slice(0, 10),
    weekday: new Date().toLocaleDateString('en-IN', { weekday: 'long' }),
    duration_min: 45,
    medium: 'odia',
    dg: sp.get('dg') || '',
    cg: sp.get('cg') || '',
    lo: sp.get('lo') || '',
    lo_code: sp.get('lo_code') || '',
    extra_context: '',
  });
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (user) {
      axios.get(`${API}/lesson-plan/history`, { withCredentials: true }).then(r => setHistory(r.data)).catch(() => {});
    }
  }, [user, plan]);

  const generate = async () => {
    if (!user) { login(); return; }
    if (!form.chapter_title || !form.topic_title) {
      toast({ title: 'Field ଅଧୁରା', description: 'Chapter ଓ Topic ଆବଶ୍ୟକ' });
      return;
    }
    setGenerating(true);
    setPlan(null);
    try {
      const res = await axios.post(`${API}/lesson-plan/generate`, form, { withCredentials: true });
      setPlan(res.data);
      toast({ title: 'Lesson Plan ପ୍ରସ୍ତୁତ ✅' });
    } catch (e) {
      toast({ title: 'ତ୍ରୁଟି', description: e?.response?.data?.detail || 'AI generation ଫେଲ୍ ହୋଇଛି। ପୁଣି try କରନ୍ତୁ।' });
    } finally { setGenerating(false); }
  };

  const copyMd = () => {
    if (plan?.content_markdown) {
      navigator.clipboard.writeText(plan.content_markdown);
      toast({ title: 'Copied ✅' });
    }
  };

  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const printPdf = () => {
    // Print-to-PDF: open a printable window (all dynamic fields HTML-escaped)
    if (!plan?.content_markdown) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const html = `<!doctype html><html><head><title>${plan.plan_type === 'full' ? 'Full' : 'Short'} Lesson Plan · Class ${esc(plan.class)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  body { font-family: 'Noto Sans Oriya', 'Poppins', sans-serif; line-height: 1.6; color: #111; }
  h1 { color: #059669; border-bottom: 3px solid #059669; padding-bottom: 8px; }
  h2 { color: #0891b2; margin-top: 20px; padding-left: 8px; border-left: 4px solid #0891b2; }
  hr { border: none; border-top: 1px dashed #ccc; margin: 20px 0; }
  .header { text-align: center; margin-bottom: 20px; }
  .attr { font-size: 11px; color: #666; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 10px; }
  pre { white-space: pre-wrap; font-family: inherit; }
</style></head><body>
<div class="header">
  <h1>Gurusevakendra · ${plan.plan_type === 'full' ? 'ପୂର୍ଣ୍ଣ' : 'ସଂକ୍ଷିପ୍ତ'} Lesson Plan</h1>
  <p><strong>Class:</strong> ${esc(plan.class)} · <strong>Subject:</strong> ${esc(plan.subject_name)} · <strong>Duration:</strong> ${esc(plan.duration_min)} min</p>
  <p><strong>Date:</strong> ${esc(plan.date || '-')} · <strong>Day:</strong> ${esc(plan.weekday || '-')}</p>
</div>
<pre>${esc(plan.content_markdown)}</pre>
<div class="attr">${esc(plan.attribution || '')}<br>Source: ${esc(plan.source || 'Gurusevakendra')} · Generated: ${esc(new Date(plan.created_at).toLocaleString('en-IN'))}</div>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium" data-testid="lp-back">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 60%, #f97316 100%)' }}>
        <Sparkles size={26} />
        <h1 className="text-xl font-extrabold mt-2">🤖 AI Lesson Plan Generator</h1>
        <p className="text-xs text-white/90 mt-1">SCERT-aligned · Odia + English · Full 14-section କିମ୍ବା Short version</p>
      </div>

      {/* Plan type toggle */}
      <div className="flex gap-2">
        {['full', 'short'].map(t => (
          <button key={t} onClick={() => upd('plan_type', t)} data-testid={`plan-type-${t}`}
            className={`flex-1 py-2 rounded-xl text-sm font-bold ${form.plan_type === t ? 'bg-purple-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-700'}`}>
            {t === 'full' ? '📘 Full Plan (14 sections)' : '📄 Short Plan'}
          </button>
        ))}
      </div>

      <Card className="rounded-2xl p-4 border-gray-200 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] font-bold text-gray-700">ଶ୍ରେଣୀ / Class</label>
            <select value={form.class_num} onChange={e => upd('class_num', parseInt(e.target.value, 10))} data-testid="lp-class"
              className="mt-1 w-full h-10 rounded-lg border border-gray-300 px-2 text-sm">
              {[1,2,3,4,5,6,7,8,9,10].map(c => <option key={c} value={c}>{`Class ${c}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-700">ମାଧ୍ୟମ / Medium</label>
            <select value={form.medium} onChange={e => upd('medium', e.target.value)} data-testid="lp-medium"
              className="mt-1 w-full h-10 rounded-lg border border-gray-300 px-2 text-sm">
              <option value="odia">ଓଡ଼ିଆ</option>
              <option value="english">English</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] font-bold text-gray-700">ବିଷୟ / Subject</label>
            <Input value={form.subject_name} onChange={e => upd('subject_name', e.target.value)} data-testid="lp-subject" placeholder="e.g. ଗଣିତ" className="mt-1 h-10 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-[11px] font-bold text-gray-700">ଅଧ୍ୟାୟ / Chapter *</label>
            <Input value={form.chapter_title} onChange={e => upd('chapter_title', e.target.value)} data-testid="lp-chapter" placeholder="e.g. ଭଗ୍ନାଂଶ" className="mt-1 h-10 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-[11px] font-bold text-gray-700">ପ୍ରସଙ୍ଗ / Topic *</label>
            <Input value={form.topic_title} onChange={e => upd('topic_title', e.target.value)} data-testid="lp-topic" placeholder="e.g. ସମାନ ଭଗ୍ନାଂଶ" className="mt-1 h-10 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-700">ତାରିଖ</label>
            <Input type="date" value={form.date} onChange={e => upd('date', e.target.value)} className="mt-1 h-10 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-700">ଅବଧି (min)</label>
            <Input type="number" value={form.duration_min} onChange={e => upd('duration_min', parseInt(e.target.value || '45', 10))} className="mt-1 h-10 text-sm" />
          </div>
        </div>

        <details className="border-t pt-2 border-gray-100">
          <summary className="text-xs font-bold text-gray-700 cursor-pointer">⚙ Advanced (DG / CG / LO / LO Code) — optional</summary>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input value={form.dg} onChange={e => upd('dg', e.target.value)} placeholder="DG" data-testid="lp-dg" className="h-10 text-sm" />
            <Input value={form.cg} onChange={e => upd('cg', e.target.value)} placeholder="CG" data-testid="lp-cg" className="h-10 text-sm" />
            <Input value={form.lo} onChange={e => upd('lo', e.target.value)} placeholder="LO" data-testid="lp-lo" className="h-10 text-sm" />
            <Input value={form.lo_code} onChange={e => upd('lo_code', e.target.value)} placeholder="LO Code" data-testid="lp-lo-code" className="h-10 text-sm font-mono" />
          </div>
          <div className="mt-2">
            <Input value={form.extra_context} onChange={e => upd('extra_context', e.target.value)} placeholder="Extra context (optional)" className="h-10 text-sm" />
          </div>
          <p className="text-[10px] text-amber-700 bg-amber-50 p-2 rounded mt-2">
            <AlertCircle size={11} className="inline mr-1" />
            Blank ରଖିଲେ AI suggest କରିବ। Verified codes ପାଇଁ SCERT Odisha ଆଡ଼କୁ ଯାଆନ୍ତୁ।
          </p>
        </details>

        <Button onClick={generate} disabled={generating} data-testid="lp-generate-btn"
          className="w-full h-12 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-base">
          {generating ? (<><Loader2 className="animate-spin mr-2" size={18}/> AI ଉତ୍ପାଦନ କରୁଛି...</>) : (<><Sparkles className="mr-2" size={18}/> Generate Lesson Plan</>)}
        </Button>
      </Card>

      {/* Generated plan */}
      {plan && (
        <Card className="rounded-2xl p-4 border-purple-200 bg-purple-50" data-testid="lp-result">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-extrabold text-purple-900">📘 {plan.plan_type === 'full' ? 'Full' : 'Short'} Lesson Plan</div>
              <div className="text-[11px] text-purple-700">Class {plan.class} · {plan.subject_name} · {plan.topic_title}</div>
            </div>
            <div className="flex gap-1.5">
              <button onClick={copyMd} data-testid="lp-copy" className="p-2 rounded-lg bg-white border border-purple-300 text-purple-700 hover:bg-purple-100"><Copy size={14}/></button>
              <button onClick={printPdf} data-testid="lp-print" className="p-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"><Download size={14}/></button>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-purple-200 max-h-[500px] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-xs text-gray-800 font-sans">{plan.content_markdown}</pre>
          </div>
          <p className="text-[10px] text-purple-700 mt-2 italic">{plan.attribution}</p>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="rounded-2xl p-4 border-gray-200">
          <div className="font-bold text-sm text-gray-900 mb-2">📚 ପୂର୍ବ Lesson Plans</div>
          <div className="space-y-2">
            {history.slice(0, 5).map(h => (
              <div key={h.id} className="p-2 bg-gray-50 rounded-lg flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-gray-900 truncate">{h.topic_title}</div>
                  <div className="text-[10px] text-gray-500">Class {h.class} · {h.subject_name} · {new Date(h.created_at).toLocaleDateString('en-IN')}</div>
                </div>
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-bold">{h.plan_type}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
